// Pixel-space force/relaxation solver that keeps vehicle ID labels from
// overlapping each other or the on-screen UI panels. Operates on label
// CENTERS with half-extents (cheap axis-aligned bounding-box math). All
// coordinates are in the same pixel frame the caller draws in (Leaflet layer
// points); the solver mutates each node's cx/cy in place.

export interface Rect {
  cx: number;
  cy: number;
  hw: number; // half width
  hh: number; // half height
}

export interface LabelNode {
  id: string;
  cx: number; // current / solved center
  cy: number;
  hw: number;
  hh: number;
  ax: number; // anchor center the label springs toward
  ay: number;
}

export interface Viewport {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export interface DeclutterOptions {
  spring?: number; // spring constant toward the anchor (low => labels roam)
  mainIters?: number; // spring + separation iterations
  finishIters?: number; // separation-only passes to clear residual touches
  pad?: number; // extra gap enforced between boxes, px
}

const DEFAULTS = {
  spring: 0.04,
  mainIters: 36,
  finishIters: 6,
  pad: 3,
};

// Push two label nodes apart along their smaller-overlap axis.
function separatePair(a: LabelNode, b: LabelNode, pad: number): void {
  const dx = b.cx - a.cx;
  const dy = b.cy - a.cy;
  const overlapX = a.hw + b.hw + pad - Math.abs(dx);
  const overlapY = a.hh + b.hh + pad - Math.abs(dy);
  if (overlapX <= 0 || overlapY <= 0) return; // not overlapping
  if (overlapX < overlapY) {
    const push = (overlapX / 2) * (dx < 0 ? -1 : 1);
    a.cx -= push;
    b.cx += push;
  } else {
    const push = (overlapY / 2) * (dy < 0 ? -1 : 1);
    a.cy -= push;
    b.cy += push;
  }
}

// Eject a label fully out of an obstacle rectangle along the smaller axis.
function avoidObstacle(n: LabelNode, o: Rect, pad: number): void {
  const dx = n.cx - o.cx;
  const dy = n.cy - o.cy;
  const overlapX = n.hw + o.hw + pad - Math.abs(dx);
  const overlapY = n.hh + o.hh + pad - Math.abs(dy);
  if (overlapX <= 0 || overlapY <= 0) return;
  if (overlapX < overlapY) {
    n.cx += dx < 0 ? -overlapX : overlapX;
  } else {
    n.cy += dy < 0 ? -overlapY : overlapY;
  }
}

function clampToViewport(n: LabelNode, vp: Viewport): void {
  n.cx = Math.max(vp.minX + n.hw, Math.min(vp.maxX - n.hw, n.cx));
  n.cy = Math.max(vp.minY + n.hh, Math.min(vp.maxY - n.hh, n.cy));
}

export function declutterLabels(
  labels: LabelNode[],
  obstacles: Rect[],
  viewport: Viewport,
  options: DeclutterOptions = {},
): void {
  const { spring, mainIters, finishIters, pad } = { ...DEFAULTS, ...options };

  const constrain = () => {
    for (let i = 0; i < labels.length; i++) {
      for (let j = i + 1; j < labels.length; j++) {
        separatePair(labels[i], labels[j], pad);
      }
    }
    for (const n of labels) {
      for (const o of obstacles) avoidObstacle(n, o, pad);
      clampToViewport(n, viewport);
    }
  };

  // Main relaxation: weak spring toward the anchor, then separation/obstacle/
  // clamp. The low spring constant lets crowded labels migrate into open space.
  for (let iter = 0; iter < mainIters; iter++) {
    for (const n of labels) {
      n.cx += (n.ax - n.cx) * spring;
      n.cy += (n.ay - n.cy) * spring;
    }
    constrain();
  }

  // Finishing passes with no spring, so the anchor pull can't reintroduce the
  // overlaps the last separation pass just removed.
  for (let iter = 0; iter < finishIters; iter++) {
    constrain();
  }
}
